import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { getAspectRatioFromSource } from '@src/utility/compositionSettings';
import { formatStoryteller } from '@src/utility/shareableImages';
import { createRef } from 'react';
import styled, { css } from 'styled-components';

import { AdditionalAspectRatio, AspectRatio } from '../../../types.ts/video';
import {
  getPosterUrl,
  getShareableImageDimension,
} from '../../../utility/general';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import DimensionsSelector from '../DimensionsSelector';
import ShareModal from '../ShareModal';
import SocialShareStatus from '../SocialShareStatus';
import { useQuoteCardRenderer } from './useQuoteCardRenderer';

import type {
  Artifact,
  Caption,
  ShareableImagePreviewType,
  ShareableImageType,
  SharedContent,
} from '../../../types.ts/story';
import type { ExtendedAspectRatio } from '../../../types.ts/video';
import type { CompositionTemplate } from '../../sidepanel/CompositionSettings/types';
import type { Dispatch, SetStateAction } from 'react';

type Props = {
  sharablePost: {
    id: string;
    quote: string;
    storytellerName?: string;
    imagefile?: Artifact;
    _allReferencingSharedContents?: SharedContent[];
    _allReferencingCaptions?: Caption[];
    source?: Record<string, any>;
  };
  toggleShare: Dispatch<SetStateAction<ShareableImageType | null>>;
  compositionTemplate: CompositionTemplate;
};

const SharableImagesModal = (props: Props) => {
  const userIdentity = useUserIdentity();
  const imageRef = createRef<HTMLImageElement>();

  const { contentStudioEnableImageShare, enableSharing } = useFlagsCombination(
    userIdentity.currentRole,
  );

  const dimension = getShareableImageDimension(
    getAspectRatioFromSource(props.sharablePost.source || {}),
  );

  const background = getPosterUrl(
    props.sharablePost.imagefile?.url || '',
    dimension.width!,
    dimension.height!,
    false,
  );

  const clip = {
    id: props.sharablePost.id,
    quote: props.sharablePost.quote,
    url: background,
    imageFile: props.sharablePost.imagefile,
    _allReferencingSharedContents:
      props.sharablePost._allReferencingSharedContents,
    _allReferencingCaptions: props.sharablePost._allReferencingCaptions,
  };

  const { name, org } = formatStoryteller(props.sharablePost.storytellerName);

  // Initialize Creatomate renderer using custom hook
  const {
    rendererContainerRef,
    updateAspectRatio,
    currentAspectRatio,
    rendererRef,
  } = useQuoteCardRenderer({
    text: props.sharablePost.quote,
    background,
    name,
    org,
    compositionTemplate: props.compositionTemplate,
    quoteCardSource: props.sharablePost.source || {},
  });

  return (
    <ShareModal
      onClose={() => props.toggleShare(null)}
      shareableImageToggle={props.toggleShare}
      shareable={clip as ShareableImagePreviewType}
      selectedDimension={currentAspectRatio}
      hideShareButton={!contentStudioEnableImageShare}
      imageRef={imageRef}
      imageSource={rendererRef.current?.getSource()}
    >
      <Main>
        <TopRow>
          {enableSharing && (
            <SocialShareStatus
              sharedContents={clip?._allReferencingSharedContents}
              isImage={true}
              showOnlyShared={true}
            />
          )}
          <Dimensions>
            <DimensionsSelector
              dimensions={[
                ...Object.values(AspectRatio),
                ...Object.values(AdditionalAspectRatio),
              ].map(aspectRatio => ({
                aspectRatio,
                isPublished: true,
              }))}
              selectedDimension={currentAspectRatio}
              onSelectDimension={(dimension: string) =>
                updateAspectRatio(dimension as ExtendedAspectRatio)
              }
              theme="green"
            />
          </Dimensions>
        </TopRow>
        <ContentArea>
          <RendererContainer
            ref={rendererContainerRef}
            aspectRatio={
              currentAspectRatio.split(':').join('/') as
                | '1/1'
                | '16/9'
                | '9/16'
                | '4/5'
            }
          />
        </ContentArea>
      </Main>
    </ShareModal>
  );
};

export default SharableImagesModal;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const TopRow = styled.div`
  display: flex;
  gap: 10px;
`;

const Dimensions = styled.div`
  margin-left: auto;
`;

const ContentArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RendererContainer = styled.div<{
  aspectRatio?: '1/1' | '16/9' | '9/16' | '4/5';
}>`
  position: relative;
  width: 100%;
  height: 100%;
  max-width: ${props =>
    props.aspectRatio === '1/1'
      ? '540px'
      : props.aspectRatio === '16/9'
        ? '960px'
        : props.aspectRatio === '4/5'
          ? '432px'
          : '304px'};
  max-height: 540px;
  overflow: hidden;
  object-fit: cover;
  border-radius: 8px;
  ${props =>
    props.aspectRatio &&
    css`
      aspect-ratio: ${props.aspectRatio};
    `}
`;
